import React, { useEffect, useMemo, useState } from "react";
import { Loader2, ShieldAlert, ExternalLink, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

const RISK = {
  High: "text-red-700 bg-red-50 border-red-200",
  Medium: "text-amber-700 bg-amber-50 border-amber-200",
  Low: "text-slate-600 bg-slate-50 border-slate-200",
};
const STATUS_LABEL = {
  not_reviewed: "Never reviewed",
  requires_review: "Requires review",
  requires_confirmation: "Requires confirmation",
  reviewed: "Reviewed",
  missing_price: "Missing price",
  missing_renewal: "Missing renewal price",
  missing_review_date: "Missing review date",
  stale_review: "Review > 6 months",
  unpublished: "Unpublished",
};
const FILTERS = [
  ["all", "All"],
  ["claim", "Strong claims"],
  ["pricing", "Missing / unconfirmed price"],
  ["review", "Needs review"],
  ["governance", "Governance"],
];

export default function AdminReview() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/admin/review-report").then((r) => setData(r.data)).catch(() => setData({ rows: [], legacy_urls: [] }));
  }, []);

  const rows = data?.rows || [];
  const shown = useMemo(() => (filter === "all" ? rows : rows.filter((r) => r.type === filter)), [rows, filter]);

  if (data === null) return <div className="mt-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-orange-600" /></div>;

  return (
    <div className="mt-8" data-testid="admin-review">
      <div className="flex items-center gap-2 mb-1"><ShieldAlert className="h-5 w-5 text-orange-600" /><h2 className="font-heading text-xl text-foreground">Pricing & compliance review</h2></div>
      <p className="text-sm text-muted-foreground mb-5">Strong claims, unconfirmed/missing pricing and overdue reviews across jurisdiction content. Flagged for manual review — a disclaimer alone does not resolve a claim. Editing content does not mark legal or pricing review complete.</p>

      <div className="flex flex-wrap gap-2 mb-5" data-testid="review-filters">
        {FILTERS.map(([k, l]) => {
          const n = k === "all" ? rows.length : rows.filter((r) => r.type === k).length;
          return (
            <button key={k} onClick={() => setFilter(k)} className={`rounded-full border px-3 py-1 text-xs transition-colors ${filter === k ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground border-border hover:text-foreground"}`} data-testid={`review-filter-${k}`}>{l} ({n})</button>
          );
        })}
      </div>

      {shown.length === 0 ? (
        <div className="rounded-sm border border-green-200 bg-green-50 p-5 text-sm text-green-800" data-testid="review-empty">No items in this view. Content reads as review-ready.</div>
      ) : (
        <div className="overflow-x-auto rounded-sm border border-border">
          <table className="w-full text-sm" data-testid="review-table">
            <thead className="bg-secondary/60 text-left">
              <tr>
                <th className="px-4 py-3 font-medium text-primary">Page</th>
                <th className="px-4 py-3 font-medium text-primary">Claim / Issue</th>
                <th className="px-4 py-3 font-medium text-primary">Risk</th>
                <th className="px-4 py-3 font-medium text-primary">Status</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((r, i) => (
                <tr key={i} className="border-t border-border" data-testid={`review-row-${i}`}>
                  <td className="px-4 py-3"><a href={r.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-orange-700 hover:underline">{r.item} <ExternalLink className="h-3 w-3" /></a></td>
                  <td className="px-4 py-3 text-muted-foreground">{r.claim}</td>
                  <td className="px-4 py-3"><span className={`inline-block rounded-full border px-2 py-0.5 text-xs ${RISK[r.risk] || RISK.Low}`}>{r.risk}</span></td>
                  <td className="px-4 py-3 text-muted-foreground">{STATUS_LABEL[r.status] || r.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-10">
        <h3 className="font-heading text-lg text-foreground mb-1">Legacy URL migration</h3>
        <p className="text-sm text-muted-foreground mb-4">Client-side redirects are live in the SPA. Apply matching permanent 301s at your CDN/hosting (e.g. Cloudflare redirect rules) for full SEO preservation.</p>
        <div className="overflow-x-auto rounded-sm border border-border">
          <table className="w-full text-sm" data-testid="legacy-url-table">
            <thead className="bg-secondary/60 text-left">
              <tr>
                <th className="px-4 py-3 font-medium text-primary">Old URL</th>
                <th className="px-4 py-3 font-medium text-primary"></th>
                <th className="px-4 py-3 font-medium text-primary">Redirect target</th>
                <th className="px-4 py-3 font-medium text-primary">Action</th>
              </tr>
            </thead>
            <tbody>
              {(data.legacy_urls || []).map((l, i) => (
                <tr key={i} className="border-t border-border" data-testid={`legacy-row-${i}`}>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{l.old}</td>
                  <td className="px-2 py-3"><ArrowRight className="h-3.5 w-3.5 text-muted-foreground" /></td>
                  <td className="px-4 py-3 font-mono text-xs text-foreground">{l.target}</td>
                  <td className="px-4 py-3 text-muted-foreground">{l.action}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
